import React, { useState } from 'react';
import { Box, Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Typography, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CreateOrganization } from '../../../../services/api';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const CreateOrganizationForm = ({ onClose, onSuccess }) => {
    const navigate = useNavigate();

    const validationSchema = Yup.object({
        organizationName: Yup.string()
            .trim()
            .min(2, 'Name must be at least 2 characters')
            .max(200, 'Name must not exceed 200 characters')
            .required('Organization Name is required'),
        organizationCode: Yup.string()
            .trim()
            .min(2, 'Code must be at least 2 characters')
            .max(50, 'Code must not exceed 50 characters')
            .required('Organization Code is required'),
        organizationType: Yup.string().required('Organization Type is required'),
        // orgAddress array validation
        orgAddress: Yup.array()
            .of(Yup.string().trim().required('Address cannot be empty'))
            .min(1, 'At least one address is required'),
        gstNumber: Yup.string().optional(),
        description: Yup.string().max(1000, 'Description too long').optional(),
    });

    const formik = useFormik({
        initialValues: {
            organizationName: '',
            organizationCode: '',
            organizationType: 'DE',
            description: '',
            gstNumber: '',
            orgAddress: [''], // Initial empty address
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            // Filter empty addresses just in case, though validation handles required.
            const validAddresses = values.orgAddress.filter(addr => addr && addr.trim() !== '');

            const payload = {
                organizationName: values.organizationName,
                organizationCode: values.organizationCode,
                organizationType: values.organizationType,
                description: values.description,
                gstNumber: values.gstNumber,
                orgAddress: validAddresses,
                isActive: true
            };

            try {
                const token = localStorage.getItem('accessToken');
                const response = await CreateOrganization(payload, token);

                if (response && (response.status === 200 || response.status === 201)) {
                    if (onSuccess) {
                        onSuccess();
                    }
                    if (onClose) {
                        onClose();
                    } else {
                        navigate('/settings/master-data');
                    }
                } else {
                    console.error('Failed to create organization:', response);
                    alert(response?.response?.data?.message || 'Failed to create organization');
                }
            } catch (error) {
                console.error('Error creating organization:', error);
                alert('An error occurred while creating organization');
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleAddressChange = (index, value) => {
        const updatedAddresses = [...formik.values.orgAddress];
        updatedAddresses[index] = value;
        formik.setFieldValue('orgAddress', updatedAddresses);
    };

    const addAddressField = () => {
        formik.setFieldValue('orgAddress', [...formik.values.orgAddress, '']);
    };

    const removeAddressField = (index) => {
        const updatedAddresses = formik.values.orgAddress.filter((_, i) => i !== index);
        formik.setFieldValue('orgAddress', updatedAddresses);
    };

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        id="organizationName"
                        name="organizationName"
                        label="Organization Name"
                        value={formik.values.organizationName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.organizationName && Boolean(formik.errors.organizationName)}
                        helperText={formik.touched.organizationName && formik.errors.organizationName}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        id="organizationCode"
                        name="organizationCode"
                        label="Organization Code"
                        value={formik.values.organizationCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.organizationCode && Boolean(formik.errors.organizationCode)}
                        helperText={formik.touched.organizationCode && formik.errors.organizationCode}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel>Organization Type</InputLabel>
                        <Select
                            id="organizationType"
                            name="organizationType"
                            value={formik.values.organizationType}
                            label="Organization Type"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        >
                            <MenuItem value="DE">DE</MenuItem>
                            <MenuItem value="Zoho">Zoho</MenuItem>
                            <MenuItem value="Non">Non</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        id="gstNumber"
                        name="gstNumber"
                        label="GST Number"
                        value={formik.values.gstNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.gstNumber && Boolean(formik.errors.gstNumber)}
                        helperText={formik.touched.gstNumber && formik.errors.gstNumber}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        id="description"
                        name="description"
                        label="Description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.description && Boolean(formik.errors.description)}
                        helperText={formik.touched.description && formik.errors.description}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>Organization Addresses</Typography>
                    {formik.values.orgAddress.map((addr, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                label={`Address ${index + 1}`}
                                value={addr}
                                onChange={(e) => handleAddressChange(index, e.target.value)}
                                required={index === 0} // HTML5 required for first one as fallback, but Yup handles it
                                error={formik.touched.orgAddress && formik.errors.orgAddress && formik.errors.orgAddress[index]}
                                helperText={formik.touched.orgAddress && formik.errors.orgAddress && formik.errors.orgAddress[index]}
                            />
                            {formik.values.orgAddress.length > 1 && (
                                <IconButton onClick={() => removeAddressField(index)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                    <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={addAddressField}
                    >
                        Add Address
                    </Button>
                    {typeof formik.errors.orgAddress === 'string' && (
                        <Typography color="error" variant="caption" display="block">{formik.errors.orgAddress}</Typography>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={formik.isSubmitting || !formik.isValid}
                    >
                        {formik.isSubmitting ? 'Creating...' : 'Create Organization'}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CreateOrganizationForm;
